import { IntulseIntegration } from "@/schema/intulse-integration"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { apiContextFetch } from "@/hooks/api/base"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

interface AuthUriResponse {
  auth_uri: string
}

export const INTULSE_INTEGRATIONS_QUERY_KEY = ["intulse-integrations"]

export function useFetchIntulseIntegrations(enabled = true) {
  const getContext = useApiContext()
  const bookId = useBookId()

  return useQuery<IntulseIntegration[]>({
    queryKey: [...INTULSE_INTEGRATIONS_QUERY_KEY, bookId],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      const data = await apiContextFetch<IntulseIntegration[]>(context, "intulse_integrations")
      return data
    },
    enabled,
  })
}

export function useGetIntulseAuthUri() {
  const getContext = useApiContext()

  return useMutation<AuthUriResponse, Error, { returnUrl?: string } | void>({
    mutationKey: ["intulse-auth-uri"],
    mutationFn: async (params = {}) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      const searchParams = new URLSearchParams()
      if (params && "returnUrl" in params && params.returnUrl) {
        searchParams.append("return_url", params.returnUrl)
      }

      const url = `intulse_integrations/get_auth_uri${searchParams.toString() ? `?${searchParams.toString()}` : ""}`
      return apiContextFetch<AuthUriResponse>(context, url)
    },
  })
}

export function useDisconnectIntulseIntegration() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation<void, Error, { id: string }>({
    mutationKey: ["intulse-integration-disconnect"],
    mutationFn: async ({ id }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      await apiContextFetch(context, `intulse_integrations/${id}`, {
        method: "PUT",
        body: JSON.stringify({ disconnect: true }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTULSE_INTEGRATIONS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ["sms-messages"] })
      queryClient.invalidateQueries({ queryKey: ["call-logs"] })
    },
  })
}

export function useConfigureIntulseSMSHistory() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation<IntulseIntegration, Error, { id: string; organizationApiKey: string }>({
    mutationKey: ["intulse-sms-history-configure"],
    mutationFn: async ({ id, organizationApiKey }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      return apiContextFetch<IntulseIntegration>(context, `intulse_integrations/${id}`, {
        method: "PUT",
        body: JSON.stringify({ master_api_key: organizationApiKey }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTULSE_INTEGRATIONS_QUERY_KEY })
    },
  })
}

export function useDeleteIntulseIntegration() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation<void, Error, { id: string }>({
    mutationFn: async ({ id }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      await apiContextFetch(context, `intulse_integrations/${id}`, {
        method: "DELETE",
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTULSE_INTEGRATIONS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ["sms-messages"] })
      queryClient.invalidateQueries({ queryKey: ["call-logs"] })
    },
  })
}
